/**
 * Klaviyo Back in Stock Integration
 * Handles API calls to Klaviyo's back in stock endpoint
 * Shopify Theme: Flex by Out of the Sandbox
 */

class KlaviyoBackInStock {
  constructor() {
    this.forms = document.querySelectorAll('[data-klaviyo-bis-form]');
    this.apiEndpoint = 'https://a.klaviyo.com/onsite/components/back-in-stock/subscribe';
    this.platform = 'shopify';

    if (this.forms.length > 0) {
      this.init();
    }
  }

  init() {
    this.forms.forEach(form => {
      form.addEventListener('submit', (e) => this.handleSubmit(e));

      // Initialize bot protection timestamp
      const timestampInput = form.querySelector('[data-klaviyo-bis-timestamp]');
      if (timestampInput) {
        timestampInput.value = Date.now().toString();
      }
    });

    // Listen for variant changes
    this.setupVariantListener();
  }

  setupVariantListener() {
    // Listen for variant selection changes
    document.addEventListener('variant-selection:changed', (event) => {
      const variantId = event.detail?.variant?.id;
      const variantAvailable = event.detail?.variant?.available;

      if (variantId) {
        this.updateVariant(variantId, variantAvailable);
      }
    });

    // Also listen for legacy variant change events
    const variantSelects = document.querySelectorAll('variant-selection');
    variantSelects.forEach(select => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'variant') {
            const variantId = select.getAttribute('variant');
            if (variantId && variantId !== 'not-selected') {
              this.checkVariantAvailability(variantId);
            }
          }
        });
      });

      observer.observe(select, { attributes: true });
    });
  }

  checkVariantAvailability(variantId) {
    // Get product form to check variant availability
    const productForm = document.querySelector('[data-product-form]');
    if (!productForm) return;

    const variantInventory = productForm.getAttribute('data-variant-inventory');
    if (!variantInventory) return;

    try {
      const inventory = JSON.parse(variantInventory);
      const variant = inventory.find(v => v.id == variantId);

      if (variant) {
        const isAvailable = this.isVariantAvailable(variant);
        this.updateVariant(variantId, isAvailable);
      }
    } catch (error) {
      console.error('Error parsing variant inventory:', error);
    }
  }

  isVariantAvailable(variant) {
    if (variant.inventory_management === null || variant.inventory_management === '') {
      return true;
    }

    if (variant.inventory_policy === 'continue') {
      return true;
    }

    return variant.inventory_quantity > 0;
  }

  updateVariant(variantId, isAvailable) {
    // Update hidden variant inputs
    const variantInputs = document.querySelectorAll('[data-klaviyo-bis-variant]');
    variantInputs.forEach(input => {
      input.value = variantId;
    });

    // Show or hide the back in stock form based on availability
    const forms = document.querySelectorAll('.klaviyo-bis-form');
    forms.forEach(form => {
      if (isAvailable) {
        form.style.display = 'none';
      } else {
        form.style.display = 'block';
      }
    });
  }

  async handleSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const formWrapper = form.closest('.klaviyo-bis-form');
    const emailInput = form.querySelector('[data-klaviyo-bis-email]');
    const variantInput = form.querySelector('[data-klaviyo-bis-variant]');
    const submitButton = form.querySelector('[data-klaviyo-bis-submit]');
    const submitText = submitButton.querySelector('.klaviyo-bis-form__submit-text');
    const loadingText = submitButton.querySelector('.klaviyo-bis-form__loading');
    const successMessage = formWrapper.querySelector('[data-klaviyo-bis-success]');
    const errorMessage = form.querySelector('[data-klaviyo-bis-error]');

    // Get values
    const email = emailInput.value.trim();
    const variantId = variantInput.value;
    const accountId = formWrapper.getAttribute('data-klaviyo-account-id');

    // Bot protection validation
    const botCheckResult = this.checkBotProtection(form, emailInput, errorMessage);
    if (!botCheckResult.passed) {
      return;
    }

    // Validate email
    if (!email || !this.isValidEmail(email)) {
      this.showError(errorMessage, emailInput, 'Please enter a valid email address.');
      return;
    }

    // Validate variant
    if (!variantId) {
      this.showError(errorMessage, emailInput, 'Please select a product variant.');
      return;
    }

    if (!accountId) {
      this.showError(errorMessage, emailInput, 'Klaviyo account ID is not configured.');
      console.error('Klaviyo account ID missing. Please add it to theme settings.');
      return;
    }

    // Show loading state - Update ARIA attributes
    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');
    emailInput.setAttribute('aria-invalid', 'false');
    submitText.style.display = 'none';
    loadingText.style.display = 'inline';
    loadingText.setAttribute('aria-hidden', 'false');
    successMessage.style.display = 'none';
    errorMessage.style.display = 'none';

    try {
      // Make API call to Klaviyo
      const response = await this.subscribeToBackInStock(accountId, email, variantId);

      if (response.success) {
        // Update rate limit tracker
        this.updateRateLimit();

        // Reset timestamp for next submission
        const timestampInput = form.querySelector('[data-klaviyo-bis-timestamp]');
        if (timestampInput) {
          timestampInput.value = Date.now().toString();
        }

        this.showSuccess(successMessage, form, emailInput);
      } else {
        this.showError(errorMessage, emailInput, response.message || 'An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Klaviyo Back in Stock Error:', error);
      this.showError(errorMessage, emailInput, 'Unable to subscribe at this time. Please try again later.');
    } finally {
      // Reset button state and ARIA attributes
      submitButton.disabled = false;
      submitButton.setAttribute('aria-busy', 'false');
      submitText.style.display = 'inline';
      loadingText.style.display = 'none';
      loadingText.setAttribute('aria-hidden', 'true');
    }
  }

  async subscribeToBackInStock(accountId, email, variantId) {
    try {
      // Klaviyo Back in Stock API requires specific payload format
      const payload = {
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: email
                }
              }
            },
            channels: ["EMAIL"]
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      };

      console.log('Klaviyo BIS Request:', {
        url: `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${accountId}`,
        payload: payload
      });

      // Make POST request to Klaviyo
      const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${accountId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-07-15'
        },
        body: JSON.stringify(payload)
      });

      console.log('Klaviyo BIS Response Status:', response.status);

      // Parse response data first
      const responseData = await response.json().catch(() => ({}));
      console.log('Klaviyo BIS Response Data:', responseData);

      // Check response - Klaviyo returns 202 for successful subscriptions
      if (response.status === 202 || response.status === 201 || response.status === 200) {
        return {
          success: true,
          data: responseData
        };
      } else {
        console.error('Klaviyo API Error Response:', responseData);
        return {
          success: false,
          message: responseData.errors?.[0]?.detail || responseData.detail || 'Subscription failed'
        };
      }
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  checkBotProtection(form, emailInput, errorMessage) {
    // 1. Honeypot check - should be empty
    const honeypot = form.querySelector('[data-klaviyo-bis-honeypot]');
    if (honeypot && honeypot.value.trim() !== '') {
      console.warn('Bot detected: honeypot field filled');
      this.showError(errorMessage, emailInput, 'Please try again.');
      return { passed: false };
    }

    // 2. Human verification checkbox - must be checked
    const verifyCheckbox = form.querySelector('[data-klaviyo-bis-verify]');
    if (verifyCheckbox && !verifyCheckbox.checked) {
      this.showError(errorMessage, emailInput, 'Please confirm you are not a robot.');
      return { passed: false };
    }

    // 3. Time-based validation - must take at least 3 seconds
    const timestampInput = form.querySelector('[data-klaviyo-bis-timestamp]');
    if (timestampInput && timestampInput.value) {
      const formLoadTime = parseInt(timestampInput.value, 10);
      const currentTime = Date.now();
      const timeElapsed = currentTime - formLoadTime;
      const minimumTime = 3000; // 3 seconds

      if (timeElapsed < minimumTime) {
        console.warn('Bot detected: form submitted too quickly', {
          elapsed: timeElapsed,
          minimum: minimumTime
        });
        this.showError(errorMessage, emailInput, 'Please slow down and try again.');
        return { passed: false };
      }
    }

    // 4. Rate limiting - check localStorage for recent submissions
    const rateLimitKey = 'klaviyo_bis_last_submission';
    const rateLimitPeriod = 60000; // 1 minute

    try {
      const lastSubmission = localStorage.getItem(rateLimitKey);
      if (lastSubmission) {
        const lastSubmissionTime = parseInt(lastSubmission, 10);
        const currentTime = Date.now();
        const timeSinceLastSubmission = currentTime - lastSubmissionTime;

        if (timeSinceLastSubmission < rateLimitPeriod) {
          const remainingSeconds = Math.ceil((rateLimitPeriod - timeSinceLastSubmission) / 1000);
          console.warn('Rate limit exceeded', {
            timeSince: timeSinceLastSubmission,
            remaining: remainingSeconds
          });
          this.showError(
            errorMessage,
            emailInput,
            `Please wait ${remainingSeconds} seconds before submitting again.`
          );
          return { passed: false };
        }
      }
    } catch (e) {
      console.warn('localStorage not available for rate limiting:', e);
    }

    return { passed: true };
  }

  updateRateLimit() {
    const rateLimitKey = 'klaviyo_bis_last_submission';
    try {
      localStorage.setItem(rateLimitKey, Date.now().toString());
    } catch (e) {
      console.warn('localStorage not available for rate limiting:', e);
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  showSuccess(successElement, form, emailInput) {
    // Update ARIA and display
    emailInput.setAttribute('aria-invalid', 'false');
    successElement.style.display = 'block';
    form.style.display = 'none';

    // Move focus to success message for screen readers
    successElement.focus();

    // Announce to screen readers (the role="status" and aria-live="polite" handle this)

    // Reset form after 5 seconds and restore focus
    setTimeout(() => {
      emailInput.value = '';

      // Reset verification checkbox
      const verifyCheckbox = form.querySelector('[data-klaviyo-bis-verify]');
      if (verifyCheckbox) {
        verifyCheckbox.checked = false;
      }

      successElement.style.display = 'none';
      form.style.display = 'block';

      // Restore focus to email input
      emailInput.focus();
    }, 5000);
  }

  showError(errorElement, emailInput, message) {
    // Update error message text
    const errorParagraph = errorElement.querySelector('p');
    if (errorParagraph) {
      errorParagraph.textContent = message;
    } else {
      errorElement.textContent = message;
    }

    // Update ARIA states
    emailInput.setAttribute('aria-invalid', 'true');
    errorElement.style.display = 'block';

    // Move focus to email input so user can correct
    emailInput.focus();

    // Announce to screen readers (the role="alert" and aria-live="assertive" handle this)

    // Hide error after 5 seconds
    setTimeout(() => {
      errorElement.style.display = 'none';
      emailInput.setAttribute('aria-invalid', 'false');
    }, 5000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new KlaviyoBackInStock();
  });
} else {
  new KlaviyoBackInStock();
}
